/**
 * 작성자 : 한영광
 * 날짜 : 2025.08.12
 * 기능 : 마스터메뉴관리 -> LLM 데이터관리 페이지
 */

import { useEffect, useState, useRef, RefObject } from 'react';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import { useRecoilState, useRecoilValue } from 'recoil';
import styled from 'styled-components';

import { IUser, pageInfoState, userState } from '../../atoms';
import { BtnGhost, BtnLightViolet } from '../../components/Button';
import { COMCD_S_STATUS, COMCD_USE_YN, INIT_USE_YN_A, LIST_COUNT } from '../../_constants';
import { SearchOptions } from '../../assets/styles/SearchOptions';
import Tab from '../../components/Tab';
import Input from '../../components/Input';
import SelectBox from '../../components/SelectBox';
import { logPost } from '../../services/log';
import TuiGrid from '../../components/Table/TuiGrid';
import { useTranslation } from 'react-i18next';
import i18n from '../../translation/i18n';
import { apiGet } from '../../services/_common';
import { ynFilter } from '../../utils/ynFilter';
import { useFetchCommonCodeList } from '../../services/useSetCodeListInSelectBoxForm';
import useTuiGridSize from '../../hooks/useTuiGridSize';
import Portal from '../../components/Portal';
import SearchFilterModal from '../../components/Modal/SearchFilterModal';
import ImageBtnRenderer from '../../components/ImageBtnRenderer';
import FireDetectionModal from '../../components/Modal/fireDetection/FireDetectionModal';
import TestLLMModal from '../../components/Modal/TestLLMModal';
import TumbleDetectionModal from '../../components/Modal/tumbleDetection/TumbleDetectionModal';
import TestTumbleLLMModal from '../../components/Modal/TestTumbleLLMModal';

const Root = styled.div`
  .tui-container {
    flex-grow: 1;
    overflow: hidden;
    margin-left: 0.5rem;
  }
  .textBtnGroup button {
    padding: 0 0.75rem;
    width: 4.5rem;
    height: 2.5rem;
    font-size: 0.875rem;
    font-weight: 500;
    border: 1px solid var(--outline);
    background-color: var(--tonal-light);
  }

  .table {
    max-height: 65vh;
  }
  .tr {
    display: flex;
    align-items: center;
    justify-content: space-between;

    > div {
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .mtName,
    .mdName,
    .mrName,
    .bigo {
      justify-content: left;
    }
  }
`;

const Master8 = () => {
  const { t } = useTranslation();
  const [userInfo] = useRecoilState<IUser>(userState);
  const size = useOutletContext<any>();
  const tabInfo = useRecoilValue(pageInfoState);
  const [currentTabMrCd, setCurrentTabMrCd] = useState(tabInfo.defaultMrCd);
  const [searchParams] = useSearchParams();
  const tabCd = searchParams.get('tabCd');

  useEffect(() => {
    if (tabCd && tabCd !== currentTabMrCd) {
      setCurrentTabMrCd(tabCd);
    }
  }, [tabCd]);

  // ─── 공통 ────────────────────────────────────────────────────────────
  const [listCount, setListCount] = useState({ type: LIST_COUNT, [LIST_COUNT]: '15', cdName: '15' });
  const [sStatus, setSStatus] = useState({ type: COMCD_S_STATUS, [COMCD_S_STATUS]: 'A', cdName: t('전체') });
  const [useYn, setUseYn] = useState(INIT_USE_YN_A);
  const [excelBtn, setExcelBtn] = useState(false);
  const [searchOption, setSearchOption] = useState({ hName: '', sName: '', bigo: '' });
  const [openModal, setOpenModal] = useState<any>({ status: false, type: '' });

  const { data: useYnComCdListWithAll } = useFetchCommonCodeList(COMCD_USE_YN, true);
  const { data: sStatusComCdListWithAll } = useFetchCommonCodeList(COMCD_S_STATUS, true);
  const { data: listCountComCdList } = useFetchCommonCodeList(LIST_COUNT, false);
  const componentRef = useRef<HTMLDivElement>(null);
  const { tuiHeight } = useTuiGridSize(componentRef);

  // 첫 번째 탭이 화재 감지, 두 번째 탭이 쓰러짐 감지
  const isFireTab = tabInfo.tabList.length === 0 || currentTabMrCd === tabInfo.tabList[0]?.mrCd;

  // ─── 화재 감지 ───────────────────────────────────────────────────────
  const [fireColumns, setFireColumns] = useState<any[]>([]);
  const [fireTableState, setFireTableState] = useState<any[]>([]);
  const [fireInitTableState, setFireInitTableState] = useState<any[]>([]);
  const [fireRowKey, setFireRowKey] = useState<any>(0);

  // ─── 쓰러짐 감지 ─────────────────────────────────────────────────────
  const [tumbleColumns, setTumbleColumns] = useState<any[]>([]);
  const [tumbleTableState, setTumbleTableState] = useState<any[]>([]);
  const [tumbleInitTableState, setTumbleInitTableState] = useState<any[]>([]);
  const [tumbleRowKey, setTumbleRowKey] = useState<any>(0);

  const buildColumns = () => [
    {
      header: t('사진'),
      name: 'imageUrl',
      align: 'center',
      renderer: { type: ImageBtnRenderer, options: { onImgButtonClicked: () => {} } },
    },
    { header: t('분석결과'), name: 'detectionResult', align: 'center', sortable: true, minWidth: 120 },
    { header: t('실제결과'), name: 'actualResult', align: 'center', sortable: true, minWidth: 120 },
    { header: t('LLM판단근거'), name: 'llmReasoning', sortable: true, minWidth: 350 },
    { header: t('사용자피드백'), name: 'userFeedback', align: 'center', sortable: true, minWidth: 350 },
    { header: t('오분류원인'), name: 'misclassificationCause', align: 'center', sortable: true, minWidth: 80 },
    { header: t('감지일시'), name: 'detectionTime', sortable: true, minWidth: 120 },
    { header: t('수정일시'), name: 'correctionTime', sortable: true, minWidth: 120 },
  ];

  useEffect(() => {
    setFireColumns(buildColumns());
    setTumbleColumns(buildColumns());
  }, [i18n.language]);

  useEffect(() => {
    const isFire = tabInfo.tabList.length === 0 || currentTabMrCd === tabInfo.tabList[0]?.mrCd;
    if (isFire) {
      getFireDataAPI().then((res: any) => {
        if (res.status === 200) {
          logPost({ hCd: userInfo.hCd, sCd: userInfo.sCd, userId: userInfo.userId, menu: 'LLM 데이터관리', action: '조회', etc: '' });
        }
      });
    } else {
      getTumbleDataAPI();
    }
  }, [currentTabMrCd]);

  const getFireDataAPI = async () => {
    const res = await apiGet({ path: '/llm/fireDetectionResults' });
    const { data, statusCode } = res.data;
    if (statusCode === 200) {
      setFireTableState(data);
      setFireInitTableState(data);
    }
    return res;
  };

  const getTumbleDataAPI = async () => {
    const res = await apiGet({ path: '/llm/tumbleDetectionResults' });
    const { data, statusCode } = res.data;
    if (statusCode === 200) {
      setTumbleTableState(data);
      setTumbleInitTableState(data);
    }
    return res;
  };

  const handleSearch = (filterRef?: RefObject<HTMLDivElement>) => {
    if (filterRef) {
      filterRef.current?.classList.remove('active');
      setOpenModal({ status: false, type: 'filter' });
    }
    const filterOptions = { hName: searchOption.hName, sName: searchOption.sName, bigo: searchOption.bigo };

    const applyFilter = (initData: any[]) => {
      const filtered = initData.filter(
        (item: any) =>
          item.hName?.toLowerCase()?.includes(filterOptions.hName?.toLowerCase()) &&
          item.sName?.toLowerCase()?.includes(filterOptions.sName?.toLowerCase()) &&
          item.bigo?.toLowerCase()?.includes(filterOptions.bigo?.toLowerCase())
      );
      const sFiltered = ynFilter(filtered, 'sStatus', sStatus[COMCD_S_STATUS]);
      return ynFilter(sFiltered, 'useYn', useYn[COMCD_USE_YN]);
    };

    if (isFireTab) {
      const result = applyFilter(fireInitTableState);
      setFireTableState(result.length > 0 ? result : []);
    } else {
      const result = applyFilter(tumbleInitTableState);
      setTumbleTableState(result.length > 0 ? result : []);
    }
  };

  const onEnterKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') handleSearch();
  };

  useEffect(() => {
    if (size.innerSize.W > 1024) handleSearch();
  }, [searchOption, useYn[COMCD_USE_YN], sStatus[COMCD_S_STATUS]]);

  const onClickFireRow = (rowData: any, _columnName: string, filteredIndex: number) => {
    setFireRowKey(filteredIndex);
    setOpenModal({ status: true, type: 'fireDetection', data: rowData });
  };

  const onClickTumbleRow = (rowData: any, _columnName: string, filteredIndex: number) => {
    setTumbleRowKey(filteredIndex);
    setOpenModal({ status: true, type: 'tumbleDetection', data: rowData });
  };

  const onClickInitiateSearchOption = () => {
    setSearchOption({ hName: '', sName: '', bigo: '' });
    setSStatus({ type: COMCD_S_STATUS, [COMCD_S_STATUS]: 'A', cdName: t('전체') });
    if (isFireTab) {
      getFireDataAPI();
    } else {
      getTumbleDataAPI();
    }
  };

  const onClickTab = (mrCd: string) => setCurrentTabMrCd(mrCd);
  const onClickFilter = () => setOpenModal({ status: true, type: 'filter' });

  const onClickTestLLM = () => {
    if (isFireTab) {
      setOpenModal({ status: true, type: 'testllm' });
    } else {
      setOpenModal({ status: true, type: 'testtumblellm' });
    }
  };

  const currentTableState = isFireTab ? fireTableState : tumbleTableState;
  const currentColumns = isFireTab ? fireColumns : tumbleColumns;
  const currentRowKey = isFireTab ? fireRowKey : tumbleRowKey;

  return (
    <div className='contents'>
      <Tab tabList={tabInfo.tabList} currentTabMrCd={currentTabMrCd} setCurrentTabMrCd={setCurrentTabMrCd} onClickTab={onClickTab} size={size} />
      <div className='content-container oneColumn'>
        <Root>
          <SearchOptions>
            {size.innerSize.W > 1024 ? (
              <div className='inputsWrapper'>
                <div className='flex-basic textBtnGroup'>
                  <BtnGhost onClick={onClickInitiateSearchOption}>{t('초기화')}</BtnGhost>
                </div>
                <div className='flex-basic textBtnGroup'>
                  <BtnLightViolet onClick={onClickTestLLM}>{t('테스트')}</BtnLightViolet>
                </div>
                <div className='inputForm-row'>
                  <div className='inputForm-col withLabelComCf'>
                    <label htmlFor='useYn'>{t('검색줄수')}</label>
                    <SelectBox
                      options={listCountComCdList}
                      defaultOption={listCount.cdName}
                      stateKey={LIST_COUNT}
                      state={listCount}
                      setState={setListCount}
                      initiateKey={listCount[LIST_COUNT]}
                      filterbar='filter-1-left'
                    />
                  </div>
                </div>
              </div>
            ) : (
              <div className='filter-wrapper'>
                <div className='inputsWrapper'>
                  <div className='inputForm-row' style={{ width: 'calc(100% - 3.5rem)' }}>
                    <div className='inputForm-col'>
                      <Input label='' placeholder={t('현장명')} type='search' id='sName' name='sName' state={searchOption} setState={setSearchOption} onKeyDown={onEnterKeyDown} ico='search' />
                    </div>
                  </div>
                  <BtnGhost onClick={onClickFilter} className='filter-btn'>
                    <span className='material-symbols-rounded'>filter_alt</span>
                  </BtnGhost>
                </div>
              </div>
            )}
          </SearchOptions>

          <div ref={componentRef} className='tui-container'>
            <TuiGrid
              data={currentTableState}
              rowKey={currentRowKey}
              columns={currentColumns}
              perPage={Number(listCount[LIST_COUNT])}
              excelBtn={excelBtn}
              setExcelBtn={setExcelBtn}
              usePagenation
              onClickRow={isFireTab ? onClickFireRow : onClickTumbleRow}
              height={tuiHeight}
              frozenCount={1}
            />
          </div>
        </Root>
      </div>

      <Portal openModal={openModal?.status}>
        {openModal.status && openModal.type === 'fireDetection' && (
          <FireDetectionModal
            refetch={getFireDataAPI}
            reportArray={fireTableState}
            setReportArray={setFireTableState}
            setOpenModal={setOpenModal}
            index={fireRowKey}
            setRowKey={setFireRowKey}
            listCount={Number(listCount[LIST_COUNT])}
          />
        )}
        {openModal.status && openModal.type === 'tumbleDetection' && (
          <TumbleDetectionModal
            refetch={getTumbleDataAPI}
            reportArray={tumbleTableState}
            setReportArray={setTumbleTableState}
            setOpenModal={setOpenModal}
            index={tumbleRowKey}
            setRowKey={setTumbleRowKey}
            listCount={Number(listCount[LIST_COUNT])}
          />
        )}
        {openModal.status && openModal.type === 'testllm' && <TestLLMModal refetch={getFireDataAPI} setOpenModal={setOpenModal} />}
        {openModal.status && openModal.type === 'testtumblellm' && <TestTumbleLLMModal refetch={getTumbleDataAPI} setOpenModal={setOpenModal} />}
      </Portal>

      {size.innerSize.W <= 1024 && (
        <Portal openModal>
          <SearchFilterModal openModal={openModal} setOpenModal={setOpenModal} onClickSearch={handleSearch} onClickReset={onClickInitiateSearchOption}>
            <div className='filter-option-wrapper'>
              <div className='filter-option-cont'>
                <div className='filter-option-row'>
                  <label htmlFor='useYn'>{t('보기 설정')}</label>
                  <SelectBox
                    options={listCountComCdList}
                    defaultOption={listCount.cdName}
                    stateKey={LIST_COUNT}
                    state={listCount}
                    setState={setListCount}
                    initiateKey={listCount[LIST_COUNT]}
                    filterbar='filter-1-left'
                  />
                </div>
                <div className='filter-option-row'>
                  <label htmlFor='sStatus'>{t('운영상태')}</label>
                  <SelectBox
                    options={sStatusComCdListWithAll}
                    defaultOption={sStatus.cdName}
                    stateKey={COMCD_S_STATUS}
                    state={sStatus}
                    setState={setSStatus}
                    initiateKey={sStatus[COMCD_S_STATUS]}
                    filterbar='filter-1-leftToCenter'
                  />
                </div>
                <div className='filter-option-row'>
                  <label htmlFor='useYn'>{t('사용유무')}</label>
                  <SelectBox
                    options={useYnComCdListWithAll}
                    defaultOption={useYn.cdName}
                    stateKey={COMCD_USE_YN}
                    state={useYn}
                    setState={setUseYn}
                    initiateKey={useYn[COMCD_USE_YN]}
                    filterbar='filter-1-center'
                  />
                </div>
              </div>
            </div>
          </SearchFilterModal>
        </Portal>
      )}
    </div>
  );
};

export default Master8;
